import classNames from 'classnames';
import { type ChangeEventHandler, type Ref, useState } from 'react';

import { FeatureFlag } from '@revelio/core';
import { Input, type useDisclosure } from '@revelio/ui';

import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';
import { ALL_FILTER_DEFINITIONS } from '../engine/filterRegistry';
import {
  FilterId,
  type FilterSelectOptions,
  FilterType,
} from '../types/filters';
import { EditFiltersButton } from './EditFiltersButton';
import { FilterSelectFooter } from './FilterSelectFooter';
import styles from './FilterSelectList.module.css';
import layoutStyles from './filter-select-list.module.css';

interface FilterSelectListProps {
  filterOptions: FilterSelectOptions[];
  setSelectedFilter: (filter: FilterSelectOptions) => void;
  closeMenu: () => void;
  /**
   * Opens the edit-filters modal, which the owning popover renders beside
   * itself: it closes to make room for the modal, and a closed surface's
   * content unmounts.
   */
  editFiltersModalDisclosure?: ReturnType<typeof useDisclosure>;
  searchInputRef?: Ref<HTMLInputElement>;
}

export const FilterSelectList = ({
  filterOptions,
  setSelectedFilter,
  closeMenu,
  editFiltersModalDisclosure,
  searchInputRef,
}: FilterSelectListProps) => {
  const [search, setSearch] = useState('');
  const [optionsStack, setOptionsStack] = useState<FilterSelectOptions[][]>([
    filterOptions,
  ]);
  const [filteredParentName, setFilteredParentName] = useState<string | null>(
    null
  );

  const handleSearch: ChangeEventHandler<HTMLInputElement> = (e) => {
    setSearch(e.target.value);
  };

  const currentOptions = optionsStack[optionsStack.length - 1] ?? [];

  const filteredFilterOptions = currentOptions?.filter((option) =>
    option.name.toLowerCase().includes(search.toLowerCase())
  );

  const isValuationEnabled = useUnleashFlag(
    FeatureFlag.CompanyDiscoveryValuationFilter
  );
  const handleFilterSelect = (filter: FilterSelectOptions) => {
    const filterId: FilterId = filter.id;
    const filterDefinition = ALL_FILTER_DEFINITIONS[filterId];

    if (filterDefinition?.type !== FilterType.GROUP) {
      setSelectedFilter(filter);
      return;
    }

    // Group filter clicked - show only its children
    const childOptions: FilterSelectOptions[] = (
      filterDefinition.children || []
    )
      .filter(
        (childId: FilterId) =>
          isValuationEnabled || childId !== FilterId.VALUATION
      )
      .map((childId: FilterId): FilterSelectOptions | null => {
        const childDef = ALL_FILTER_DEFINITIONS[childId];
        return childDef ? { id: childId, name: childDef.name } : null;
      })
      .filter((option): option is FilterSelectOptions => option !== null);

    setOptionsStack([...optionsStack, childOptions]);
    setFilteredParentName(filter.name);
    setSearch('');
  };

  const handleBack = () => {
    if (optionsStack.length > 1) {
      setOptionsStack(optionsStack.slice(0, optionsStack.length - 1));
      setFilteredParentName(null);
      setSearch('');
    }
  };

  return (
    <>
      <div>
        <Input
          ref={searchInputRef}
          placeholder={
            filteredParentName
              ? `${filteredParentName} filters`
              : 'Select a filter'
          }
          size="sm"
          onChange={handleSearch}
          value={search}
          wrapperClassName={styles.searchInput}
        />
      </div>
      <div data-testid="filter-menu-popover">
        <ul className={classNames(layoutStyles.list, styles.list)}>
          {filteredFilterOptions.length === 0 && (
            <li className={layoutStyles.emptyItem}>No options</li>
          )}

          {filteredFilterOptions.map((option) => (
            <li
              key={option.id}
              onClick={() => handleFilterSelect(option)}
              className={classNames(layoutStyles.item, styles.listItem)}
              data-testid="select-filter-option"
            >
              <div
                role="button"
                tabIndex={-1}
                className={layoutStyles.itemLabel}
              >
                {option.name}
              </div>
            </li>
          ))}
        </ul>
      </div>
      <div className={styles.footer}>
        {editFiltersModalDisclosure && (
          <EditFiltersButton onOpenModal={editFiltersModalDisclosure.onOpen} />
        )}
        <div className={layoutStyles.footer}>
          <FilterSelectFooter
            onClose={closeMenu}
            onBack={optionsStack.length > 1 ? handleBack : undefined}
            isAddDisabled
          />
        </div>
      </div>
    </>
  );
};
